import React, { useState } from "react";
import {
  ArrowLeft, Save, UserPlus, Users, Trash2, RefreshCw, Calendar, Clock, Mail, Phone, MapPin, Building, Plus, Search
} from "lucide-react";

const AdminClientsDashboard = () => {
  const [selectedClient, setSelectedClient] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedClientData, setEditedClientData] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("recent");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);

  const [clients, setClients] = useState([
    {
      id: "client-001",
      name: "Emma Thompson",
      email: "emma.thompson@email.com",
      phone: "+44 20 1234 5678",
      status: "active",
      photo: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?q=80&w=1888&auto=format&fit=crop&ixlib=rb-4.0.3",
      company: "TechVision Solutions Ltd",
      title: "Senior Project Manager",
      location: "London, United Kingdom",
      activeProjects: 1,
      lastActivity: "2 hours ago",
      joined: "September 2023",
      verified: true,
      completionRate: 98,
      totalSpent: 125000,
      totalReviews: 45,
      profileData: {
        personal: {
          name: "Emma Thompson",
          title: "Senior Project Manager",
          photo: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?q=80&w=1888&auto=format&fit=crop&ixlib=rb-4.0.3",
          coverPhoto: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3",
          location: "London, United Kingdom",
          bio: "Experienced project manager with 10+ years specializing in digital transformation and innovation initiatives.",
          email: "emma.thompson@email.com",
          phone: "+44 20 1234 5678",
          joined: "September 2023",
          lastActive: "2 hours ago",
          verified: true,
          projectsPosted: 15,
          hiredProfessionals: 12,
          successRate: 95,
          totalSpent: 125000,
          preferredLanguages: ["English (Native)", "French (Professional)", "German (Basic)"],
          socialLinks: {
            linkedin: "https://linkedin.com/in/emmathompson",
            website: "https://emmathompson.com",
          },
          company: {
            name: "TechVision Solutions Ltd",
            industry: "Technology & Software",
            size: "50-100 employees",
            founded: "2018",
            website: "https://techvision.com",
            description: "Leading digital transformation consultancy specializing in enterprise solutions",
          },
        },
        stats: {
          totalInvested: "£250,000+",
          avgProjectValue: "£15,000",
          completionRate: 98,
          repeatHireRate: 85,
          avgRating: 4.9,
          totalReviews: 45,
        },
        projectPreferences: {
          projectTypes: ["Web Development", "Mobile Apps", "UI/UX Design", "Cloud Infrastructure", "AI/ML Integration"],
          industries: ["Technology", "Finance", "E-commerce", "Healthcare", "Education"],
        },
        hiring: {
          activeJobs: 3,
          totalHires: 25,
          successfulProjects: 22,
          preferredSkills: ["React.js", "Node.js", "Python", "AWS", "UI/UX Design", "Mobile Development"],
          activeJobsData: [
            {
              id: "JOB-2024-001",
              title: "Senior UX/UI Designer",
              location: "Remote",
              salary: "£5,000-7,000",
              postedDate: "5 days ago",
              description: "Looking for an experienced UX/UI designer to revamp our e-commerce platform with a focus on mobile responsiveness and accessibility.",
              skills: ["UI/UX Design", "Figma", "Adobe XD", "Mobile Design"],
              applicants: 12,
              interviewing: 4,
              finalStage: 0,
              status: "active"
            },
            {
              id: "JOB-2024-002",
              title: "Full Stack React/Node.js Developer",
              location: "London/Remote",
              salary: "£7,500-10,000",
              postedDate: "2 weeks ago",
              description: "We need an experienced full-stack developer to join our team for the next phase of our e-commerce platform development. Three-month contract with possibility of extension.",
              skills: ["React.js", "Node.js", "MongoDB", "AWS", "TypeScript"],
              applicants: 24,
              interviewing: 6,
              finalStage: 2,
              status: "active"
            },
            {
              id: "JOB-2024-003",
              title: "DevOps Engineer",
              location: "Remote",
              salary: "£8,000-12,000",
              postedDate: "3 days ago",
              description: "Seeking a DevOps engineer to help optimize our CI/CD pipeline and cloud infrastructure. Focus on AWS, Docker, and Kubernetes.",
              skills: ["AWS", "Docker", "Kubernetes", "CI/CD", "Terraform"],
              applicants: 8,
              interviewing: 2,
              finalStage: 0,
              status: "active"
            }
          ],
          previousHires: [
            {
              id: "HIRE-2024-001",
              name: "John Doe",
              role: "Senior Full Stack Developer",
              project: "E-commerce Platform Development",
              startDate: "January 2024",
              photo: "https://randomuser.me/api/portraits/men/1.jpg",
              status: "active"
            },
            {
              id: "HIRE-2023-015",
              name: "Sarah Wilson",
              role: "UI/UX Designer",
              project: "E-commerce Platform Development",
              startDate: "January 2024",
              photo: "https://randomuser.me/api/portraits/women/1.jpg",
              status: "active"
            },
            {
              id: "HIRE-2023-014",
              name: "Michael Chen",
              role: "Backend Developer",
              project: "API Optimization",
              startDate: "November 2023",
              endDate: "January 2024",
              photo: "https://randomuser.me/api/portraits/men/4.jpg",
              status: "completed"
            }
          ]
        },
        reviews: {
          professional: [
            {
              id: 1,
              freelancer: { name: "John Doe", photo: "https://randomuser.me/api/portraits/men/1.jpg", title: "Senior Full Stack Developer" },
              rating: 5,
              date: "2024-01-15",
              project: "E-commerce Platform Development",
              comment: "Emma is an exceptional client to work with. Clear communication and timely feedback made the project a great success.",
            },
          ],
        },
        activeProjects: [
          {
            id: "PRJ-2024-001",
            title: "E-commerce Platform Development",
            status: "in_progress",
            completion: 75,
            budget: 25000,
            timeline: "3 months",
            startDate: "2024-01-01",
            dueDate: "2024-03-31",
            team: [
              { name: "John Doe", role: "Lead Developer", photo: "https://randomuser.me/api/portraits/men/1.jpg" },
              { name: "Sarah Wilson", role: "UI/UX Designer", photo: "https://randomuser.me/api/portraits/women/1.jpg" },
            ],
            technologies: ["React", "Node.js", "MongoDB", "AWS"],
            nextMilestone: "Development Phase Completion",
          },
        ],
        achievements: [
          { title: "Top Client 2023", description: "Recognized for exceptional project management and professional relationships" },
          { title: "95% Success Rate", description: "Maintained high project success rate across all engagements" },
          { title: "Team Builder", description: "Successfully built and managed diverse remote teams" },
        ],
      }
    },
    {
      id: "client-002",
      name: "Alexander Brown",
      email: "alex.brown@company.co.uk",
      phone: "+44 20 8765 4321",
      status: "active",
      photo: "https://randomuser.me/api/portraits/men/32.jpg",
      company: "Global Finance Partners",
      title: "Finance Director",
      location: "Manchester, United Kingdom",
      activeProjects: 2,
      lastActivity: "1 day ago",
      joined: "June 2023",
      verified: true,
      completionRate: 92,
      totalSpent: 80000,
      totalReviews: 18
    },
    {
      id: "client-003",
      name: "Sophia Chen",
      email: "sophia.chen@techinnovate.com",
      phone: "+44 77 1234 5678",
      status: "inactive",
      photo: "https://randomuser.me/api/portraits/women/44.jpg",
      company: "TechInnovate Solutions",
      title: "CTO",
      location: "Birmingham, United Kingdom",
      activeProjects: 0,
      lastActivity: "3 months ago",
      joined: "January 2023",
      verified: true,
      completionRate: 100,
      totalSpent: 150000,
      totalReviews: 22
    },
    {
      id: "client-004",
      name: "James Wilson",
      email: "james.wilson@retailgroup.com",
      phone: "+44 79 9876 5432",
      status: "active",
      photo: "https://randomuser.me/api/portraits/men/45.jpg",
      company: "Retail Group International",
      title: "Marketing Director",
      location: "Edinburgh, Scotland",
      activeProjects: 3,
      lastActivity: "5 hours ago",
      joined: "March 2023",
      verified: true,
      completionRate: 85,
      totalSpent: 95000,
      totalReviews: 32
    },
    {
      id: "client-005",
      name: "Olivia Davis",
      email: "olivia.davis@healthcare.org",
      phone: "+44 78 5555 4444",
      status: "pending",
      photo: "https://randomuser.me/api/portraits/women/22.jpg",
      company: "National Healthcare Initiative",
      title: "Head of Digital Transformation",
      location: "Cardiff, Wales",
      activeProjects: 0,
      lastActivity: "Just joined",
      joined: "March 2024",
      verified: false,
      completionRate: 0,
      totalSpent: 0,
      totalReviews: 0
    }
  ]);

  const handleEditClient = (client) => {
    setSelectedClient(client);
    setEditedClientData(JSON.parse(JSON.stringify(client.profileData)));
    setIsEditing(true);
  };

  const confirmDeleteClient = (client) => {
    setClientToDelete(client);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteClient = () => {
    if (clientToDelete) {
      setClients(clients.filter(c => c.id !== clientToDelete.id));
      if (selectedClient && selectedClient.id === clientToDelete.id) {
        setSelectedClient(null);
      }
      setClientToDelete(null);
      setDeleteConfirmOpen(false);
    }
  };

  const handleSaveChanges = () => {
    const updatedClients = clients.map(client => {
      if (client.id === selectedClient.id) {
        const updatedClient = {
          ...client,
          name: editedClientData.personal.name,
          email: editedClientData.personal.email,
          phone: editedClientData.personal.phone,
          title: editedClientData.personal.title,
          company: editedClientData.personal.company.name,
          location: editedClientData.personal.location,
          photo: editedClientData.personal.photo,
          profileData: editedClientData
        };
        return updatedClient;
      }
      return client;
    });
    
    setClients(updatedClients);
    setSelectedClient({
      ...selectedClient,
      name: editedClientData.personal.name,
      email: editedClientData.personal.email,
      phone: editedClientData.personal.phone,
      title: editedClientData.personal.title,
      company: editedClientData.personal.company.name,
      location: editedClientData.personal.location,
      photo: editedClientData.personal.photo,
      profileData: editedClientData
    });
    setIsEditing(false);
  };

  const handleEditField = (section, field, value) => {
    setEditedClientData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleEditNestedField = (section, nestedSection, field, value) => {
    setEditedClientData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [nestedSection]: {
          ...prev[section][nestedSection],
          [field]: value
        }
      }
    }));
  };

  const handleBackToList = () => {
    setSelectedClient(null);
    setIsEditing(false);
    setEditedClientData(null);
  };

  const filterClients = () => {
    let filteredClients = [...clients];
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filteredClients = filteredClients.filter(client => 
        client.name.toLowerCase().includes(query) || 
        client.email.toLowerCase().includes(query) || 
        client.company.toLowerCase().includes(query)
      );
    }
    
    if (statusFilter !== "all") {
      filteredClients = filteredClients.filter(client => client.status === statusFilter);
    }
    
    if (sortBy === "recent") {
      filteredClients.sort((a, b) => {
        if (a.lastActivity === "Just joined") return -1;
        if (b.lastActivity === "Just joined") return 1;
        if (a.lastActivity.includes("hour") && !b.lastActivity.includes("hour")) return -1;
        if (!a.lastActivity.includes("hour") && b.lastActivity.includes("hour")) return 1;
        return 0;
      });
    } else if (sortBy === "spending") {
      filteredClients.sort((a, b) => b.totalSpent - a.totalSpent);
    } else if (sortBy === "name") {
      filteredClients.sort((a, b) => a.name.localeCompare(b.name));
    }
    
    return filteredClients;
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case "active":
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">{status}</span>;
      case "inactive":
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">{status}</span>;
      case "pending":
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">{status}</span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50"  style={{ textAlign: "left" }}> 
      <div className="container mx-auto py-6 px-4 max-w-7xl">
        {selectedClient ? (
          <div>
            <div className="mb-6 flex items-center justify-between">
              <button onClick={() => window.history.back()} className="inline-flex items-center justify-center h-10 py-2 px-4 rounded-md text-sm font-medium border border-gray-300 hover:bg-gray-100">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </button>
              
              <div className="flex items-center gap-2">
                {isEditing ? (
                  <>
                    <button onClick={() => { setIsEditing(false); setEditedClientData(null); }} className="inline-flex items-center justify-center h-10 py-2 px-4 rounded-md text-sm font-medium border border-gray-300 hover:bg-gray-100">
                      Cancel
                    </button>
                    <button onClick={handleSaveChanges} className="inline-flex items-center justify-center h-10 py-2 px-4 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-700">
                      <Save className="mr-2 h-4 w-4" />
                      Save Changes
                    </button>
                  </>
                ) : (
                  <>
                    <button onClick={() => handleEditClient(selectedClient)} className="inline-flex items-center justify-center h-10 py-2 px-4 rounded-md text-sm font-medium border border-gray-300 hover:bg-gray-100">
                      Edit Profile
                    </button>
                    <button onClick={() => confirmDeleteClient(selectedClient)} className="inline-flex items-center justify-center h-10 py-2 px-4 rounded-md text-sm font-medium bg-red-600 text-white hover:bg-red-700">
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete Client
                    </button>
                  </>
                )}
              </div>
            </div>
            
            {isEditing ? (
              <div className="bg-white rounded-lg shadow-sm border">
                <div className="p-4 border-b">
                  <div className="grid grid-cols-5 gap-4">
                    <button className="text-sm font-medium bg-gray-100 rounded-md p-2">Personal Info</button>
                    <button className="text-sm font-medium bg-gray-100 rounded-md p-2">Company</button>
                    <button className="text-sm font-medium bg-gray-100 rounded-md p-2">Projects</button>
                    <button className="text-sm font-medium bg-gray-100 rounded-md p-2">Hiring</button>
                    <button className="text-sm font-medium bg-gray-100 rounded-md p-2">Achievements</button>
                  </div>
                </div>
                
                <div className="p-6">
                  <div className="space-y-4">
                    <h2 className="text-xl font-semibold">Personal Information</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Full Name</label>
                        <input 
                          className="w-full p-2 border rounded-md" 
                          value={editedClientData.personal.name} 
                          onChange={(e) => handleEditField("personal", "name", e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Professional Title</label>
                        <input 
                          className="w-full p-2 border rounded-md" 
                          value={editedClientData.personal.title} 
                          onChange={(e) => handleEditField("personal", "title", e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Email</label>
                        <input 
                          className="w-full p-2 border rounded-md" 
                          value={editedClientData.personal.email} 
                          onChange={(e) => handleEditField("personal", "email", e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Phone</label>
                        <input 
                          className="w-full p-2 border rounded-md" 
                          value={editedClientData.personal.phone} 
                          onChange={(e) => handleEditField("personal", "phone", e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Location</label>
                        <input 
                          className="w-full p-2 border rounded-md" 
                          value={editedClientData.personal.location} 
                          onChange={(e) => handleEditField("personal", "location", e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Joined Date</label>
                        <input 
                          className="w-full p-2 border rounded-md" 
                          value={editedClientData.personal.joined} 
                          onChange={(e) => handleEditField("personal", "joined", e.target.value)}
                        />
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <label className="text-sm font-medium">Bio</label>
                        <textarea 
                          className="w-full p-2 border rounded-md" 
                          rows={4}
                          value={editedClientData.personal.bio} 
                          onChange={(e) => handleEditField("personal", "bio", e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Profile Photo URL</label>
                        <input 
                          className="w-full p-2 border rounded-md" 
                          value={editedClientData.personal.photo} 
                          onChange={(e) => handleEditField("personal", "photo", e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Cover Photo URL</label>
                        <input 
                          className="w-full p-2 border rounded-md" 
                          value={editedClientData.personal.coverPhoto} 
                          onChange={(e) => handleEditField("personal", "coverPhoto", e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end mt-6">
                    <button onClick={() => { setIsEditing(false); setEditedClientData(null); }} className="inline-flex items-center justify-center h-10 py-2 px-4 rounded-md text-sm font-medium border border-gray-300 hover:bg-gray-100 mr-2">
                      Cancel
                    </button>
                    <button onClick={handleSaveChanges} className="inline-flex items-center justify-center h-10 py-2 px-4 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-700">
                      <Save className="mr-2 h-4 w-4" />
                      Save All Changes
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
                  <div className="relative h-48 bg-cover bg-center" style={{ backgroundImage: `url(${selectedClient.profileData.personal.coverPhoto})` }}>
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/70"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                      <div className="flex items-end gap-4">
                        <div className="relative -mb-2">
                          <img src={selectedClient.photo} alt={selectedClient.name} className="h-20 w-20 rounded-full border-4 border-white object-cover" />
                        </div>
                        <div>
                          <h1 className="text-2xl font-bold">{selectedClient.name}</h1>
                          <p>{selectedClient.title}</p>
                          <div className="flex items-center gap-3 mt-1 text-sm text-gray-300">
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {selectedClient.location}
                            </span>
                            <span className="flex items-center gap-1">
                              <Building className="w-3 h-3" />
                              {selectedClient.company}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                  <div className="col-span-2 rounded-lg border bg-white shadow-sm">
                    <div className="p-6">
                      <h2 className="text-lg font-semibold flex items-center justify-between">
                        Client Overview
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${selectedClient.status === "active" ? "bg-green-100 text-green-800" : selectedClient.status === "inactive" ? "bg-gray-100 text-gray-800" : "bg-yellow-100 text-yellow-800"}`}>
                          {selectedClient.status.charAt(0).toUpperCase() + selectedClient.status.slice(1)}
                        </span>
                      </h2>
                      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-y-4">
                        <div>
                          <h3 className="text-sm font-medium text-gray-500">Email</h3>
                          <p className="flex items-center gap-2 mt-1">
                            <Mail className="w-4 h-4 text-gray-400" />
                            {selectedClient.email}
                          </p>
                        </div>
                        <div>
                          <h3 className="text-sm font-medium text-gray-500">Phone</h3>
                          <p className="flex items-center gap-2 mt-1">
                            <Phone className="w-4 h-4 text-gray-400" />
                            {selectedClient.phone}
                          </p>
                        </div>
                        <div>
                          <h3 className="text-sm font-medium text-gray-500">Joined</h3>
                          <p className="flex items-center gap-2 mt-1">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            {selectedClient.joined}
                          </p>
                        </div>
                        <div>
                          <h3 className="text-sm font-medium text-gray-500">Last Activity</h3>
                          <p className="flex items-center gap-2 mt-1">
                            <Clock className="w-4 h-4 text-gray-400" />
                            {selectedClient.lastActivity}
                          </p>
                        </div>
                      </div>
                      <div className="mt-6">
                        <h3 className="text-sm font-medium text-gray-500">Bio</h3>
                        <p className="mt-2 text-gray-600">{selectedClient.profileData.personal.bio}</p>
                      </div>
                      <div className="mt-6">
                        <h3 className="text-sm font-medium text-gray-500">Company</h3>
                        <div className="mt-2 p-4 bg-gray-50 rounded-lg">
                          <h4 className="font-medium">{selectedClient.profileData.personal.company.name}</h4>
                          <p className="text-sm text-gray-600 mt-1">{selectedClient.profileData.personal.company.description}</p>
                          <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                            <div>
                              <span className="text-gray-500">Industry:</span> 
                              <span className="ml-1 font-medium">{selectedClient.profileData.personal.company.industry}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Size:</span> 
                              <span className="ml-1 font-medium">{selectedClient.profileData.personal.company.size}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Founded:</span> 
                              <span className="ml-1 font-medium">{selectedClient.profileData.personal.company.founded}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Website:</span> 
                              <a href={selectedClient.profileData.personal.company.website} target="_blank" rel="noopener noreferrer" className="ml-1 text-blue-600 hover:underline">
                                {selectedClient.profileData.personal.company.website.replace(/^https?:\/\//, "")}
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="rounded-lg border bg-white shadow-sm">
                      <div className="p-6">
                        <h2 className="text-lg font-semibold mb-4">Client Statistics</h2>
                        <div className="space-y-4">
                          <div className="flex justify-between items-center pb-2 border-b">
                            <span className="text-gray-600">Active Projects</span>
                            <span className="font-medium">{selectedClient.activeProjects}</span>
                          </div>
                          <div className="flex justify-between items-center pb-2 border-b">
                            <span className="text-gray-600">Completion Rate</span>
                            <span className="font-medium">{selectedClient.completionRate}%</span>
                          </div>
                          <div className="flex justify-between items-center pb-2 border-b">
                            <span className="text-gray-600">Total Spent</span>
                            <span className="font-medium">£{selectedClient.totalSpent.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600">Reviews</span>
                            <span className="font-medium">{selectedClient.totalReviews}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                  <div className="rounded-lg border bg-white shadow-sm">
                    <div className="p-6">
                      <h2 className="text-lg font-semibold mb-4">Active Projects ({selectedClient.profileData.activeProjects.length})</h2>
                      {selectedClient.profileData.activeProjects.map(project => (
                        <div key={project.id} className="border rounded-lg p-4 mb-3 last:mb-0">
                          <div className="flex justify-between items-start">
                            <h3 className="font-medium">{project.title}</h3>
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">In Progress</span>
                          </div>
                          <div className="mt-2 text-sm">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-gray-500">Progress:</span>
                              <span>{project.completion}%</span>
                            </div>
                            <div className="w-full h-2 bg-gray-100 rounded-full">
                              <div className="h-full bg-blue-500 rounded-full" style={{ width: `${project.completion}%` }}></div>
                            </div>
                          </div>
                          <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                            <div>
                              <span className="text-gray-500">Budget:</span> 
                              <span className="ml-1 font-medium">£{project.budget.toLocaleString()}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Timeline:</span> 
                              <span className="ml-1 font-medium">{project.timeline}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Started:</span> 
                              <span className="ml-1 font-medium">{project.startDate}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Due:</span> 
                              <span className="ml-1 font-medium">{project.dueDate}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="rounded-lg border bg-white shadow-sm">
                    <div className="p-6">
                      <h2 className="text-lg font-semibold mb-4">Active Job Listings ({selectedClient.profileData.hiring.activeJobs})</h2>
                      {selectedClient.profileData.hiring.activeJobsData.map(job => (
                        <div key={job.id} className="border rounded-lg p-4 mb-3 last:mb-0">
                          <div className="flex justify-between items-start mb-2">
                            <h3 className="font-medium">{job.title}</h3>
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Active</span>
                          </div>
                          <p className="text-sm text-gray-600 mb-2">{job.description}</p>
                          <div className="flex items-center flex-wrap gap-1 mb-2">
                            {job.skills.map(skill => (
                              <span key={skill} className="inline-flex items-center px-2 py-0.5 rounded text-xs border border-gray-300">{skill}</span>
                            ))}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {job.location}
                            </span>
                            <span className="flex items-center gap-1">
                              <span className="h-3 w-3">£</span>
                              {job.salary}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              Posted {job.postedDate}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {deleteConfirmOpen && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                <div className="bg-white rounded-lg p-6 max-w-md w-full">
                  <h3 className="text-lg font-semibold">Delete Client</h3>
                  <p className="text-sm text-gray-500 mt-2">
                    Are you sure you want to delete {clientToDelete?.name}? This action cannot be undone.
                  </p>
                  <div className="flex justify-end gap-2 mt-4">
                    <button onClick={() => setDeleteConfirmOpen(false)} className="inline-flex items-center justify-center h-10 py-2 px-4 rounded-md text-sm font-medium border border-gray-300 hover:bg-gray-100">
                      Cancel
                    </button>
                    <button onClick={handleDeleteClient} className="inline-flex items-center justify-center h-10 py-2 px-4 rounded-md text-sm font-medium bg-red-600 text-white hover:bg-red-700">
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete Client
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div>
            <div className="mb-6">
              <button onClick={() => window.history.back()} className="inline-flex items-center justify-center h-10 py-2 px-4 rounded-md text-sm font-medium border border-gray-300 hover:bg-gray-100">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </button>
            </div>
            <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Users className="h-6 w-6" />
                Client Management
              </h1>
              <button className="inline-flex items-center justify-center h-10 py-2 px-4 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-700">
                <UserPlus className="mr-2 h-4 w-4" />
                Add New Client
              </button>
            </div>
            
            <div className="rounded-lg border bg-white shadow-sm mb-6">
              <div className="p-4 flex flex-col sm:flex-row gap-4">
                <div className="relative grow">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <input 
                    placeholder="Search clients..." 
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full p-2 pl-9 border rounded-md"
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  <div className="space-y-2">
                    <div className="flex items-center gap-1">
                      <button onClick={() => setStatusFilter("all")} className={`h-9 px-3 rounded-md text-sm font-medium ${statusFilter === "all" ? "bg-blue-600 text-white" : "border border-gray-300 hover:bg-gray-100"}`}>
                        All
                      </button>
                      <button onClick={() => setStatusFilter("active")} className={`h-9 px-3 rounded-md text-sm font-medium ${statusFilter === "active" ? "bg-blue-600 text-white" : "border border-gray-300 hover:bg-gray-100"}`}>
                        Active
                      </button>
                      <button onClick={() => setStatusFilter("inactive")} className={`h-9 px-3 rounded-md text-sm font-medium ${statusFilter === "inactive" ? "bg-blue-600 text-white" : "border border-gray-300 hover:bg-gray-100"}`}>
                        Inactive
                      </button>
                      <button onClick={() => setStatusFilter("pending")} className={`h-9 px-3 rounded-md text-sm font-medium ${statusFilter === "pending" ? "bg-blue-600 text-white" : "border border-gray-300 hover:bg-gray-100"}`}>
                        Pending
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-1">
                      <button onClick={() => setSortBy("recent")} className={`h-9 px-3 rounded-md text-sm font-medium ${sortBy === "recent" ? "bg-blue-600 text-white" : "border border-gray-300 hover:bg-gray-100"}`}>
                        Recent
                      </button>
                      <button onClick={() => setSortBy("spending")} className={`h-9 px-3 rounded-md text-sm font-medium ${sortBy === "spending" ? "bg-blue-600 text-white" : "border border-gray-300 hover:bg-gray-100"}`}>
                        Spending
                      </button>
                      <button onClick={() => setSortBy("name")} className={`h-9 px-3 rounded-md text-sm font-medium ${sortBy === "name" ? "bg-blue-600 text-white" : "border border-gray-300 hover:bg-gray-100"}`}>
                        Name A-Z
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
              <div className="min-w-full overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Projects</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Spent</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Activity</th>
                      <th scope="col" className="relative px-6 py-3"><span className="sr-only">Actions</span></th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filterClients().map((client) => (
                      <tr key={client.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <img src={client.photo} alt={client.name} className="h-10 w-10 rounded-full object-cover" />
                            </div>
                            <div className="ml-4">
                              <div className="font-medium text-gray-900">{client.name}</div>
                              <div className="text-sm text-gray-500">{client.company}</div>
                              <div className="text-xs text-gray-500">{client.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(client.status)}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="text-sm text-gray-900">{client.activeProjects}</div>
                            <span className="ml-1 text-xs text-gray-500">active</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">£{client.totalSpent.toLocaleString()}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{client.lastActivity}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end gap-2">
                            <button className="inline-flex items-center justify-center h-9 px-3 rounded-md border border-gray-300 hover:bg-gray-100">
                              <span className="h-4 w-4">👁️</span>
                            </button>
                            <button className="inline-flex items-center justify-center h-9 px-3 rounded-md border border-gray-300 hover:bg-gray-100">
                              <span className="h-4 w-4">✏️</span>
                            </button>
                            <button onClick={() => confirmDeleteClient(client)} className="inline-flex items-center justify-center h-9 px-3 rounded-md border border-gray-300 text-red-600 hover:bg-red-100">
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            
            {filterClients().length === 0 && (
              <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                  <Users className="h-6 w-6 text-gray-600" />
                </div>
                <h3 className="mt-4 text-lg font-medium text-gray-900">No clients found</h3>
                <p className="mt-2 text-sm text-gray-500">
                  No clients match your current filters. Try adjusting your search or filter criteria.
                </p>
                <div className="mt-6">
                  <button 
                    onClick={() => { setSearchQuery(""); setStatusFilter("all"); setSortBy("recent"); }}
                    className="inline-flex items-center justify-center h-10 py-2 px-4 rounded-md text-sm font-medium border border-gray-300 hover:bg-gray-100"
                  >
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Reset Filters
                  </button>
                </div>
              </div>
            )}
            
            {deleteConfirmOpen && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                <div className="bg-white rounded-lg p-6 max-w-md w-full">
                  <h3 className="text-lg font-semibold">Delete Client</h3>
                  <p className="text-sm text-gray-500 mt-2">
                    Are you sure you want to delete {clientToDelete?.name}? This action cannot be undone.
                  </p>
                  <div className="flex justify-end gap-2 mt-4">
                    <button onClick={() => setDeleteConfirmOpen(false)} className="inline-flex items-center justify-center h-10 py-2 px-4 rounded-md text-sm font-medium border border-gray-300 hover:bg-gray-100">
                      Cancel
                    </button>
                    <button onClick={handleDeleteClient} className="inline-flex items-center justify-center h-10 py-2 px-4 rounded-md text-sm font-medium bg-red-600 text-white hover:bg-red-700">
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete Client
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminClientsDashboard;